import React, { useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Modal from "src/components/modal";
import GenericTable from "src/components/custom/GenericTable";
import { User, MapPin, Clock, Briefcase, UserCheck } from "lucide-react"; // Added extra icons

const Table = ({ data, loading, pagination }) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [viewImage, setViewImage] = useState(null);

  const handleViewPicture = (info) => {
    setViewImage(info);
    setOpenModal(true);
  };

  // Helper to safely format time
  const formatTime = (time) => (time ? moment(time).format("h:mm A") : <span className="text-red-500 text-xs font-medium">Pending</span>);

  const columns = [
    {
      header: t("itiHome.date"),
      render: (row) => moment(row?.date).format("DD/MM/YYYY")
    },
    {
      header: t("itiHome.picture"),
      render: (row) => (
        <img
          role="button"
          className="h-10 w-10 rounded-full object-cover border border-gray-200 hover:scale-105 transition-transform shadow-sm cursor-pointer"
          onClick={() => handleViewPicture(row)}
          src={row?.image_url}
          alt="Proof"
        />
      ),
      className: "w-16"
    },
    {
      header: t("itiHome.registrationId"),
      render: (row) => <span>{row?.trainee?.registration_id || "-"}</span>
    },
    {
      header: t("itiHome.studentName"),
      accessor: "trainee.name",
      render: (row) => <span className="font-medium text-gray-900">{row?.trainee?.name || "-"}</span>
    },
    {
      header: t("itiHome.checkIn"),
      render: (row) => moment(row?.checkin_time).format("h:mm A")
    },
    {
      header: t("itiHome.checkOut"),
      render: (row) => row?.checkout_time ? moment(row?.checkout_time).format("h:mm A") : <span className="text-red-500">Pending</span>,
    },
    {
      header: t("itiHome.trainerName"),
      render: (row) => row?.trainer_name || "-", // Fixed copy-paste error from your snippet
    },
    {
      header: t("itiHome.trade"),
      render: (row) => <div className="max-w-[120px] truncate">{row?.trade_master?.trade_name}</div>
    },
    {
      header: t("itiHome.industry"),
      render: (row) => (
        <div className="max-w-[150px] truncate" title={row?.industry_master?.industry_name || row?.name_of_industry_partner}>
          {row?.industry_master?.industry_name || row?.name_of_industry_partner}
        </div>
      )
    }
  ];

  return (
    <>
      <GenericTable 
        columns={columns} 
        data={data?.data} 
        pagination={pagination}
        // emptyMessage="No attendance records found." 
      />

      <Modal
        isOpen={openModal}
        title="Student Details"
        onClose={() => {
          setViewImage(null);
          setOpenModal(false);
        }}
      >
        <div className="flex flex-col gap-4">
            {/* Header Section */}
            <div className="bg-white pb-2 border-b border-gray-100">
                <div className="flex items-center gap-2 mb-1">
                    <User className="text-primary" />
                    <span className="text-lg font-bold text-gray-900">
                        {viewImage?.trainee?.name || viewImage?.name || "Unknown Trainee"}
                    </span>
                </div>
                <div className="flex items-start gap-2 text-sm text-gray-500">
                    <MapPin size={18} className="mt-0.5" />
                    <span className="leading-tight">
                        {viewImage?.industry_master?.industry_name || "Unknown Location"}
                    </span>
                </div>
            </div>

            {/* Image Section */}
            <div className="bg-gray-100 rounded-xl p-4 flex items-center justify-center border border-gray-200 shadow-inner min-h-[250px]">
                {viewImage?.image_url ? (
                    <img
                        className="max-w-full h-auto max-h-[50vh] object-contain rounded-lg shadow-md bg-white"
                        src={viewImage?.image_url}
                        alt="Attendance Evidence"
                    />
                ) : (
                    <span className="text-gray-400 text-sm">No Image Available</span>
                )}
            </div>

            {/* New Details Grid */}
            <div className="grid grid-cols-2 gap-3 bg-gray-50 p-3 rounded-lg border border-gray-100">
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-gray-400 mb-1 flex items-center gap-1">
                        <Clock size={12} /> Check-In
                    </span>
                    <span className="text-sm font-semibold text-gray-700">
                        {formatTime(viewImage?.checkin_time)}
                    </span>
                </div>

                <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-gray-400 mb-1 flex items-center gap-1">
                        <Clock size={12} /> Check-Out
                    </span>
                    <span className="text-sm font-semibold text-gray-700">
                        {formatTime(viewImage?.checkout_time)}
                    </span>
                </div>

                <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-gray-400 mb-1 flex items-center gap-1">
                        <UserCheck size={12} /> Trainer
                    </span>
                    <span className="text-sm font-medium text-gray-700 truncate">
                        {viewImage?.trainer_name || "-"}
                    </span>
                </div>

                <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-gray-400 mb-1 flex items-center gap-1">
                        <Briefcase size={12} /> Trade
                    </span>
                    <span className="text-sm font-medium text-gray-700 truncate">
                        {viewImage?.trade_master.trade_name || "-"}
                    </span>
                </div>
            </div>
            
            {/* Footer Date */}
            <div className="text-center text-xs text-gray-400 pt-2 border-t border-gray-100">
                Captured: {moment(viewImage?.date).format("DD MMMM YYYY, h:mm A")}
            </div>
        </div>
      </Modal>
    </>
  );
};

export default Table;